import { useState, useEffect, useCallback, useMemo } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  ExternalLink
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const STATUS_COLORS = {
  Scheduled: "bg-slate-500",
  Invoiced: "bg-blue-500",
  InvoiceRequested: "bg-indigo-500",
  PartiallyPaid: "bg-amber-500",
  Paid: "bg-emerald-500",
  Overdue: "bg-red-500",
  Waived: "bg-gray-400"
};

const STATUS_BADGE_COLORS = {
  Scheduled: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  Invoiced: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  InvoiceRequested: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
  PartiallyPaid: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
  Paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
  Overdue: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
  Waived: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
};

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
}

function formatCompact(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function DayCell({ date, milestones, isToday, isCurrentMonth, onDayClick }) {
  const dayTotal = milestones.reduce((sum, m) => sum + (m.amount_due || 0), 0);
  const hasOverdue = milestones.some(m => m.is_overdue);
  const hasPaid = milestones.some(m => m.status === 'Paid');
  const hasScheduled = milestones.some(m => m.status === 'Scheduled' || m.status === 'Invoiced');
  
  return (
    <div 
      className={`
        min-h-[100px] p-1 border-r border-b border-border/50 
        ${isCurrentMonth ? 'bg-card' : 'bg-muted/30'}
        ${isToday ? 'ring-2 ring-primary ring-inset' : ''}
        ${milestones.length > 0 ? 'cursor-pointer hover:bg-accent/50 transition-colors' : ''}
      `}
      onClick={() => milestones.length > 0 && onDayClick(date, milestones)}
      data-testid={`calendar-day-${date.toISOString().split('T')[0]}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`
          text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
          ${isToday ? 'bg-primary text-primary-foreground' : ''}
          ${!isCurrentMonth ? 'text-muted-foreground' : ''}
        `}>
          {date.getDate()}
        </span>
        {dayTotal > 0 && (
          <span className="text-[10px] font-semibold text-muted-foreground">
            {formatCompact(dayTotal)}
          </span>
        )}
      </div>
      
      {/* Milestone indicators */}
      <div className="space-y-0.5">
        {milestones.slice(0, 3).map((m, idx) => (
          <div 
            key={m.id || idx}
            className={`
              text-[10px] px-1.5 py-0.5 rounded truncate
              ${m.is_overdue ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 
                m.status === 'Paid' ? 'bg-emerald-200 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200' :
                'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}
            `}
          >
            <span className="font-medium">{formatCompact(m.amount_due || 0)}</span>
            <span className="ml-1 opacity-75">{m.program_name?.substring(0, 10)}...</span>
          </div>
        ))}
        {milestones.length > 3 && (
          <div className="text-[10px] text-muted-foreground pl-1">
            +{milestones.length - 3} more
          </div>
        )}
      </div>
    </div>
  );
}

function MilestoneDetailDialog({ open, onClose, date, milestones, onProgramClick }) {
  if (!milestones || milestones.length === 0) return null;
  
  const totalDue = milestones.reduce((sum, m) => sum + (m.amount_due || 0), 0);
  const dateStr = date?.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            {dateStr}
          </DialogTitle>
          <div className="text-sm text-muted-foreground">
            {milestones.length} milestone{milestones.length !== 1 ? 's' : ''} • {formatCurrency(totalDue)} due
          </div>
        </DialogHeader>
        
        <div className="space-y-3 mt-4">
          {milestones.map((m) => (
            <Card key={m.id} className={m.is_overdue ? 'border-red-300 dark:border-red-800' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => {
                        onProgramClick(m.program_id);
                        onClose(false);
                      }}
                      className="font-medium text-sm hover:text-primary transition-colors text-left"
                    >
                      {m.program_name}
                    </button>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {m.title} • {m.type}
                    </div>
                    {m.pm_name && (
                      <div className="text-xs text-muted-foreground">
                        PM: {m.pm_name}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold text-sm">{formatCurrency(m.amount_due)}</div>
                    <Badge className={`text-[10px] mt-1 ${STATUS_BADGE_COLORS[m.status] || STATUS_BADGE_COLORS.Scheduled}`}>
                      {m.status}
                    </Badge>
                  </div>
                </div>
                
                {m.notes && (
                  <div className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                    {m.notes}
                  </div>
                )}
                
                {m.invoice_url && (
                  <a
                    href={m.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Invoice
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MoneyCalendarView() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [pms, setPms] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMilestones, setSelectedMilestones] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status_filter", statusFilter);
      if (pmFilter !== "all") params.append("pm_filter", pmFilter);
      
      const res = await apiClient.get(`/milestones-overview?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      toast.error("Failed to load milestones");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pmFilter]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  useEffect(() => {
    apiClient.get("/users").then(res => {
      const pmList = res.data.filter(u => 
        ["COO", "Program Manager", "Marketing PM", "Sales"].includes(u.role)
      );
      setPms(pmList);
    }).catch(() => {});
  }, []);
  
  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Previous month padding
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startPad - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month days
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(year, month, d);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Next month padding
    const remaining = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  }, [year, month]);
  
  // Group milestones by date
  const milestonesByDate = useMemo(() => {
    const map = {};
    if (!data) return map;
    
    // Process all milestones from all months
    data.months?.forEach(monthData => {
      monthData.milestones?.forEach(m => {
        if (m.due_date) {
          const dateKey = m.due_date.split('T')[0];
          if (!map[dateKey]) map[dateKey] = [];
          map[dateKey].push(m);
        }
      });
    });
    
    return map;
  }, [data]);
  
  // Calculate month totals
  const monthStats = useMemo(() => {
    let totalDue = 0;
    let totalCount = 0;
    let overdueCount = 0;
    let paidCount = 0;
    
    calendarDays.forEach(({ date, isCurrentMonth }) => {
      if (!isCurrentMonth) return;
      const dateKey = date.toISOString().split('T')[0];
      const dayMilestones = milestonesByDate[dateKey] || [];
      dayMilestones.forEach(m => {
        totalCount++;
        totalDue += m.amount_due || 0;
        if (m.is_overdue) overdueCount++;
        if (m.status === 'Paid') paidCount++;
      });
    });
    
    return { totalDue, totalCount, overdueCount, paidCount };
  }, [calendarDays, milestonesByDate]);
  
  const today = new Date();
  const isToday = (date) => 
    date.getDate() === today.getDate() && 
    date.getMonth() === today.getMonth() && 
    date.getFullYear() === today.getFullYear();
  
  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  
  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  
  const handleDayClick = (date, milestones) => {
    setSelectedDay(date);
    setSelectedMilestones(milestones);
    setDialogOpen(true);
  };
  
  const handleProgramClick = (programId) => {
    navigate(`/programs/${programId}?tab=milestone-payments`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <div data-testid="money-calendar-view" className="space-y-4">
      {/* Header with month navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={handlePrevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-xl font-light min-w-[200px] text-center" style={{ fontFamily: 'var(--font-sans)' }}>
            {MONTHS[month]} {year}
          </h2>
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setCurrentDate(new Date())}
            className="ml-2"
          >
            Today
          </Button>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="invoiced">Invoiced</SelectItem>
            </SelectContent>
          </Select>
          <Select value={pmFilter} onValueChange={setPmFilter}>
            <SelectTrigger className="w-[150px] h-9">
              <SelectValue placeholder="PM" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All PMs</SelectItem>
              {pms.map(pm => (
                <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Month Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
            <DollarSign className="w-3.5 h-3.5" />
            Total Due
          </div>
          <div className="text-lg sm:text-xl font-light">{formatCurrency(monthStats.totalDue)}</div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
            <Clock className="w-3.5 h-3.5" />
            Milestones
          </div>
          <div className="text-lg sm:text-xl font-light">{monthStats.totalCount}</div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2 text-red-600 text-xs mb-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Overdue
          </div>
          <div className="text-lg sm:text-xl font-light text-red-600">{monthStats.overdueCount}</div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-2 text-emerald-600 text-xs mb-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Paid
          </div>
          <div className="text-lg sm:text-xl font-light text-emerald-600">{monthStats.paidCount}</div>
        </Card>
      </div>
      
      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0 overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-muted/50 border-b">
            {DAYS.map(day => (
              <div key={day} className="py-2 px-2 text-center text-sm font-medium text-muted-foreground border-r last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map(({ date, isCurrentMonth }, idx) => {
              const dateKey = date.toISOString().split('T')[0];
              const dayMilestones = milestonesByDate[dateKey] || [];
              
              return (
                <DayCell
                  key={idx}
                  date={date}
                  milestones={dayMilestones}
                  isToday={isToday(date)}
                  isCurrentMonth={isCurrentMonth}
                  onDayClick={handleDayClick}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/30"></div>
          <span>Overdue</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-200 dark:bg-emerald-900/50"></div>
          <span>Paid</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-100 dark:bg-emerald-900/30"></div>
          <span>Scheduled/Invoiced</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded ring-2 ring-primary"></div>
          <span>Today</span>
        </div>
      </div>
      
      {/* Day Detail Dialog */}
      <MilestoneDetailDialog
        open={dialogOpen}
        onClose={setDialogOpen}
        date={selectedDay}
        milestones={selectedMilestones}
        onProgramClick={handleProgramClick}
      />
    </div>
  );
}
